import { Tab, Tabs, useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import React from "react";
import { useTranslation } from "react-i18next";
import { FieldProperties, FieldsData, ResultSet } from "../../API/fields";
import useDronePhoto from "../../routes/customer/_customer_id/field/_field_id/useDronePhoto";
import FieldInformation from "../../routes/customer/_customer_id/field/components/FieldInformation/FieldInformation";
import FieldSelect from "../../routes/customer/_customer_id/field/components/FieldSelect/FieldSelect";
import { ResultSetSelect } from "../../routes/customer/_customer_id/field/components/ResultSet/ResultSetSelect";
import TilingSelect from "../../routes/customer/_customer_id/field/components/TilingSelect/TilingSelect";
import BaseLayer from "../Map/Layers/BaseLayer";
import MapControl from "../Map/MapControl";
import FieldMap, { FieldFeature } from "./FieldMap";
import styles from "./FieldOverviewPage.module.css";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import { useLoading } from "../LoadingSpinner/LoadingContext";
import { Collapsible, DownwardCollapsible } from "../Collapsible/Collapsible";
import {
  FieldData,
  MergeTaskmaps,
} from "../../routes/customer/_customer_id/fields/MergeTaskmaps";
import { TaskmapHistoryEntry } from "../../API/taskmapConfigHistory";
import { useRouteLoaderData } from "react-router";
import useResultSetDownload from "../../routes/customer/_customer_id/field/components/ResultSet/useResultSetDownload";

export type FieldOverviewPageProps = {
  onSelectField: (fieldId: number) => void;
  selectedResultSetId: string | undefined;
  onCreateTaskMap: (resultSetId: string, fieldId: number) => void;
  onSelectResultSet: (
    resultSetId: string | null,
    fieldId: number,
    customerId: number,
    options?: { replace: boolean },
  ) => void;
  children?: React.ReactNode;
  selectedFieldIds: number[] | undefined;
  onSelectMultipleFields: (fieldIds: number[]) => void;
  onMergeTaskmaps: () => void;
};

export default function FieldOverviewPage({
  onSelectField,
  selectedResultSetId = undefined,
  onSelectResultSet,
  children,
  onCreateTaskMap,
  onMergeTaskmaps,
  selectedFieldIds,
  onSelectMultipleFields,
}: FieldOverviewPageProps): React.ReactNode {
  const { t } = useTranslation();
  const [photo] = useDronePhoto();
  const { loading } = useLoading();

  const { fields, tilings } = useRouteLoaderData("customer") as FieldsData;

  const taskmaps = useRouteLoaderData("fields") as TaskmapHistoryEntry[];

  const selectedFields = selectedFieldIds
    ? fields.features.filter((field) =>
        selectedFieldIds.includes(field.properties.id),
      )
    : null;

  const taskmapsByFieldId = selectedFields
    ? selectedFields.reduce(
        (acc, field) => {
          const taskmapsForField = taskmaps
            ? taskmaps.filter(
                (taskmap) => taskmap.field_id === field.properties.id,
              )
            : [];
          const fieldProperties = field.properties;
          const fieldId = fieldProperties.id;
          acc[fieldId] = {
            id: fieldId,
            name: fieldProperties.name,
            taskmaps: taskmapsForField,
          };
          return acc;
        },
        {} as Record<number, FieldData>,
      )
    : null;

  const isMobile = useMediaQuery("(max-width:768px)");

  const [isFieldOpen, setFieldStatus] = React.useState(!isMobile);
  const [isTabOpen, setTabStatus] = React.useState(true);
  const [isMultipleFieldsPanelOpen, setMultipleFieldsPanelOpen] =
    React.useState(true);
  return (
    <div className={styles.fieldPage}>
      <Collapsible
        position="left"
        label={{
          open: t("field_selection.label.open"),
          close: t("field_selection.label.close"),
        }}
        className={`${styles.fieldOpen} ${!isFieldOpen ? styles.fieldClosed : ""}`}
        isOpen={isFieldOpen}
        onOpenChange={setFieldStatus}
      >
        <div className={styles.fieldSelector}>
          <FieldSelect
            featureList={fields.features}
            onSelectMultipleFields={(fieldIds: number[]) => {
              onSelectMultipleFields(fieldIds);
              if (isMobile) setFieldStatus(false);
              if (fieldIds.length === 1) setTabStatus(true);
              if (fieldIds.length > 1) setMultipleFieldsPanelOpen(true);
            }}
            selectedFieldIds={selectedFieldIds}
            maxSelected={5}
          />
        </div>
      </Collapsible>
      {loading && <LoadingSpinner />}
      <div className={styles.mapFiller}>
        <FieldMap
          selectedFieldIds={selectedFieldIds}
          onClick={(feature: FieldFeature) => {
            onSelectField(feature.properties.id);
            setTabStatus(true);
          }}
          tiling={
            tilings && tilings.find((tiling) => tiling.id.toString() === photo)
          }
          geojson={fields}
        >
          <BaseLayer />
          {children}
          <MapControl
            position="bottom-left"
            className={
              (selectedFields?.length === 1 && isTabOpen) ||
              (selectedFieldIds &&
                selectedFieldIds.length > 1 &&
                isMultipleFieldsPanelOpen)
                ? "open"
                : "collapsed"
            }
          >
            {selectedFields?.length === 1 && (
              <div className={styles.mapControlPanel}>
                <DownwardCollapsible
                  label={{
                    open: selectedFields[0].properties.name,
                    close: t("field_selection.details.panel.label.close"),
                  }}
                  className={isTabOpen ? styles.tabOpen : styles.tabClosed}
                  isOpen={isTabOpen}
                  onOpenChange={setTabStatus}
                >
                  <div className={styles.controlPane}>
                    <SelectedTabs
                      selectedField={selectedFields[0].properties}
                      selectedResultSetId={selectedResultSetId}
                      onSelectResultSet={onSelectResultSet}
                      onCreateTaskMap={onCreateTaskMap}
                      tilingSelector={
                        <TilingSelect
                          tilings={tilings}
                          bounds={selectedFields[0]}
                        />
                      }
                    />
                  </div>
                </DownwardCollapsible>
              </div>
            )}
            {selectedFieldIds &&
              selectedFieldIds.length > 1 &&
              taskmapsByFieldId && (
                <div className={styles.mapControlPanel}>
                  <DownwardCollapsible
                    label={{
                      open: t("fields_selection.details.panel.label.open"),
                      close: t("fields_selection.details.panel.label.close"),
                    }}
                    className={
                      isMultipleFieldsPanelOpen
                        ? styles.tabOpen
                        : styles.tabClosed
                    }
                    isOpen={isMultipleFieldsPanelOpen}
                    onOpenChange={setMultipleFieldsPanelOpen}
                  >
                    <div className={styles.controlPane}>
                      <MergeTaskmaps
                        taskmapsByFieldId={taskmapsByFieldId}
                        onMergeTaskmaps={onMergeTaskmaps}
                      />
                    </div>
                  </DownwardCollapsible>
                </div>
              )}
          </MapControl>
        </FieldMap>
      </div>
    </div>
  );
}

type SelectedTabsProps = Pick<
  FieldOverviewPageProps,
  "onSelectResultSet" | "selectedResultSetId" | "onCreateTaskMap"
> & {
  selectedField: FieldProperties;
  tilingSelector: React.ReactNode;
};

function SelectedTabs({
  selectedField,
  selectedResultSetId,
  onSelectResultSet,
  onCreateTaskMap,
  tilingSelector,
}: SelectedTabsProps) {
  const { t } = useTranslation();
  const resultSetDownload = useResultSetDownload();
  const [tab, setTab] = React.useState(0);
  const resultSets = useRouteLoaderData("field") as ResultSet[];

  const resultSetsForField = resultSets
    ? resultSets.filter((resultSet) => resultSet.field_id === selectedField.id)
    : [];

  return (
    <div className={styles.tabDiv}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tab} onChange={(e, value) => setTab(value)}>
          <Tab label={t("tab.Analysis results")} />
          <Tab label={t("tab.Field information")} />
        </Tabs>
      </Box>
      {tab === 1 && <FieldInformation fieldProperties={selectedField} />}
      {tab === 0 && (
        <ResultSetSelect
          key={selectedField.id}
          resultSetList={resultSetsForField}
          selectedResultSetId={selectedResultSetId}
          onSelect={(selectedResultSetId, options) =>
            onSelectResultSet(
              selectedResultSetId,
              selectedField.id,
              selectedField.customer_id,
              options,
            )
          }
          onCreateTaskMap={(selectedResultSetId) =>
            onCreateTaskMap(selectedResultSetId, selectedField.id)
          }
          onDownloadResult={resultSetDownload.download}
          downloadState={resultSetDownload.state}
          tilingSelector={tilingSelector}
        />
      )}
    </div>
  );
}
